import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { CircleCheck, CircleX, Info, TriangleAlert, X } from "lucide-react";
import type { ComponentProps, MouseEvent, ReactNode } from "react";
import { useState } from "react";

import { cn } from "helpers/cn";

// antd switches to the roomier block layout on `description`, not on `title`, so a
// description-only alert still reads as the large form.
const alertVariants = cva(
  "relative flex w-full rounded-lg border text-body-small text-foreground",
  {
    variants: {
      variant: {
        neutral: "border-border bg-muted",
        info: "border-info-border bg-info-surface",
        success: "border-success-border bg-success-surface",
        warning: "border-warning-border bg-warning-surface",
        error: "border-destructive-border bg-destructive-surface"
      },
      hasDescription: {
        true: "items-start px-6 py-5",
        false: "items-center px-3 py-2"
      },
      banner: {
        true: "rounded-none border-0"
      }
    },
    defaultVariants: { variant: "neutral", hasDescription: false }
  }
);

const iconVariants = cva("shrink-0", {
  variants: {
    variant: {
      neutral: "text-subtle",
      info: "text-info",
      success: "text-success",
      warning: "text-warning",
      error: "text-destructive"
    },
    hasDescription: {
      true: "me-3 [&>svg]:size-6",
      false: "me-2 [&>svg]:size-3.5"
    }
  },
  defaultVariants: { variant: "neutral", hasDescription: false }
});

const titleVariants = cva("", {
  variants: {
    hasDescription: {
      true: "mb-2",
      false: ""
    }
  },
  defaultVariants: { hasDescription: false }
});

type AlertVariant = NonNullable<VariantProps<typeof alertVariants>["variant"]>;

const DEFAULT_ICONS: Record<AlertVariant, ReactNode> = {
  neutral: <Info />,
  info: <Info />,
  success: <CircleCheck />,
  warning: <TriangleAlert />,
  error: <CircleX />
};

type AlertProps = Omit<ComponentProps<"div">, "title"> & {
  action?: ReactNode;
  banner?: boolean;
  closable?: boolean;
  description?: ReactNode;
  icon?: ReactNode;
  onClose?: (event: MouseEvent<HTMLButtonElement>) => void;
  showIcon?: boolean;
  title?: ReactNode;
  variant?: AlertVariant;
};

const Alert = ({
  action,
  banner,
  className,
  closable = false,
  description,
  icon,
  onClose,
  showIcon = false,
  title,
  variant = "neutral",
  ...props
}: AlertProps) => {
  const [closed, setClosed] = useState(false);

  if (closed) return null;

  const hasDescription = description != null;

  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    setClosed(true);
    onClose?.(event);
  };

  return (
    <div
      className={cn(alertVariants({ banner, hasDescription, variant }), className)}
      data-slot="alert"
      {...props}
    >
      {showIcon && (
        <span
          className={iconVariants({ hasDescription, variant })}
          data-slot="alert-icon"
        >
          {icon ?? DEFAULT_ICONS[variant]}
        </span>
      )}
      <div className="min-w-0 flex-1" data-slot="alert-section">
        {title != null && (
          <div className={titleVariants({ hasDescription })} data-slot="alert-title">
            {title}
          </div>
        )}
        {hasDescription && <div data-slot="alert-description">{description}</div>}
      </div>
      {action != null && (
        <div className="ms-2 shrink-0" data-slot="alert-actions">
          {action}
        </div>
      )}
      {closable && (
        <button
          aria-label="Close alert"
          className="ms-2 shrink-0 cursor-pointer border-0 bg-transparent p-0 text-subtle transition-colors hover:text-muted-foreground"
          data-slot="alert-close"
          type="button"
          onClick={handleClose}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export { Alert, alertVariants, iconVariants };
export type { AlertProps, AlertVariant };
